import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Pencil, Save, X } from 'lucide-react'
import { markdownComponents } from '@/config/markdownConfig'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

interface ReviewTabProps {
  isEditing?: boolean
  onEditingChange?: (editing: boolean) => void
  searchQuery?: string
  pageText: string | null
  docId?: string
  currentPage?: number
  onContentUpdate?: (newContent: string, pageNumber: number) => void
}

export function ReviewTabLegacy({ pageText, docId, currentPage, onContentUpdate }: ReviewTabProps) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Reset edit state when page changes to prevent saving to wrong page
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
      setEditedContent('')
    }
  }, [currentPage])

  const handleStartEdit = () => {
    setEditedContent(pageText || '')
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedContent('')
  }

  const handleSaveEdit = async () => {
    if (!docId || currentPage === undefined) {
      toast.error(t('common:document_viewer.toasts.cannot_save_missing_info'))
      return
    }

    setIsSaving(true)
    try {
      // Page is 0-indexed in backend, currentPage is 1-indexed
      await apiService.updateDocumentPageText(docId, currentPage - 1, editedContent)

      // Update parent component with the page that was edited (not currentPage from closure)
      if (onContentUpdate && currentPage !== undefined) {
        onContentUpdate(editedContent, currentPage)
      }

      toast.success(t('common:document_viewer.toasts.page_text_updated'))
      setIsEditing(false)
      setEditedContent('')
    } catch (error) {
      console.error('Failed to save page text:', error)
      toast.error(t('common:document_viewer.toasts.failed_to_save'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-card">
      {/* Content Area */}
      <div className="flex-1 overflow-auto relative">
        {/* Edit Button - Fixed position in top right corner */}
        {pageText && docId && currentPage !== undefined && !isEditing && (
          <div className="absolute top-4 right-4 z-10">
            <Button
              size="sm"
              onClick={handleStartEdit}
              className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
            >
              <Pencil className="w-4 h-4" />
              {t('document_viewer:review.edit', { defaultValue: 'Edit' })}
            </Button>
          </div>
        )}

        {/* Save/Cancel Buttons when editing */}
        {isEditing && (
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancelEdit}
              disabled={isSaving}
              className="flex items-center gap-2 shadow-md bg-white dark:bg-dark-card"
            >
              <X className="w-4 h-4" />
              {t('document_viewer:review.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={isSaving}
              className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
            >
              <Save className="w-4 h-4" />
              {isSaving ? t('document_viewer:review.saving', { defaultValue: 'Saving...' }) : t('document_viewer:review.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        )}

        {pageText ? (
          isEditing ? (
            <div className="h-full p-6">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-full p-4 font-mono text-sm border dark:border-dark-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-dark-card dark:text-dark-text"
                placeholder={t('document_viewer:review.editPlaceholder', { defaultValue: 'Edit the markdown content here...' })}
              />
            </div>
          ) : pageText.length > 50_000 ? (
            // Large content: skip ReactMarkdown to avoid freezing the browser
            <div className="p-6" style={{ paddingTop: docId && currentPage !== undefined ? '3.5rem' : '1.5rem' }}>
              <pre className="font-mono text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-dark-text leading-relaxed">
                {pageText}
              </pre>
            </div>
          ) : (
            <div className="p-6" style={{ paddingTop: docId && currentPage !== undefined ? '3.5rem' : '1.5rem' }}>
              <div className="markdown-content max-w-full" style={{ lineHeight: '1.6', wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {pageText}
                </ReactMarkdown>
              </div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
            {t('document_viewer:review.noContent', { defaultValue: 'No content available' })}
          </div>
        )}
      </div>
    </div>
  )
}
